import clsx from "clsx";
import { motion } from "framer-motion";

import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type TopLevelBreakdown } from "@features/sales-v3/types";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

import {
  useAvailableBreakdownConfigs,
  usePageConfiguration,
} from "../../hooks";

import { BreakdownButtonSelect } from "./breakdown-button-select";

export const BreakdownsButtonCompact = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    compareDateRange,
    dateRangeInterval,
    breakdowns,
    dateRange,
    filters,
    setBreakdowns,
  } = usePageConfiguration();

  const sensors = useSensors(useSensor(PointerSensor));

  const availableBreakdownConfigs = useAvailableBreakdownConfigs(breakdowns);

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    dateRange,
    compareDateRange,
    breakdowns,
    filters,
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      prettyLogEventPage({
        domain: "Minimized Top Bar",
        action: "Other",
        additionalActionInfo: "Reorder breakdowns",
        data: {
          newBreakdowns: breakdowns,
          pageContext,
        },
      });

      const oldIndex = breakdowns.findIndex(({ key }) => key === active.id);
      const newIndex = breakdowns.findIndex(({ key }) => key === over?.id);

      const newOrdering = arrayMove(breakdowns, oldIndex, newIndex);
      setBreakdowns(newOrdering);
    }
  };

  const areBreakdownsSelected = breakdowns.length > 0;

  return (
    <Popover className="relative z-10">
      {({ open }) => (
        <>
          <PopoverButton
            as="button"
            className={clsx(
              "text-xs button secondary-button h-7 py-0 px-3 flex items-center max-w-[120px] ml-2",
              {
                "bg-cosmic-4 border-cosmic-4 hover:bg-cosmic-3 hover:border-cosmic-3":
                  !open,
                "bg-cosmic border-cosmic text-white": open,
              },
            )}
          >
            BREAKDOWNS
            <motion.i
              initial={false}
              animate={{ rotate: open ? 0 : 180 }}
              transition={{ duration: 0.2 }}
              className={clsx(
                "fa-regular fa-sharp fa-chevron-up font-bold ml-2",
                open && "text-white",
              )}
            />
          </PopoverButton>
          <PopoverPanel className="absolute w-56 right-0 bg-white rounded-lg elevation-1 py-1">
            <div className="px-2 mb-1">
              <BreakdownButtonSelect
                buttonText="ADD BREAKDOWNS"
                breakdownConfigs={availableBreakdownConfigs}
                areBreakdownsSelected={false}
                onChange={(value) => {
                  prettyLogEventPage({
                    domain: "Minimized Top Bar",
                    action: "onSelect",
                    additionalActionInfo: "Add breakdowns",
                    data: {
                      newBreakdowns: breakdowns,
                      pageContext,
                    },
                  });

                  setBreakdowns([...breakdowns, value]);
                }}
              />
            </div>
            {areBreakdownsSelected && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
                autoScroll={false}
              >
                <SortableContext items={breakdowns.map(({ key }) => key)}>
                  {breakdowns.map((breakdown) => (
                    <BreakdownRow key={breakdown.key} breakdown={breakdown} />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

const BreakdownRow = ({ breakdown }: { breakdown: TopLevelBreakdown }) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    dateRange,
    compareDateRange,
    breakdowns,
    filters,
    setBreakdowns,
  } = usePageConfiguration();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: breakdown.key,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    dateRange,
    compareDateRange,
    breakdowns,
    filters,
  };

  return (
    <div
      className={clsx(
        "group w-full h-8 px-4 flex items-center text-sm border-none bg-white",
        "hover:bg-cosmic-4 cursor-pointer",
      )}
    >
      <span
        className="truncate flex-1 w-full text-sm"
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
      >
        <i className="fa-regular fa-sharp fa-grip-dots text-primary cursor-pointer mr-2" />
        {breakdown.key}
      </span>
      {!attributes["aria-pressed"] && (
        <GenericTooltip
          className="hidden group-hover:block"
          noInfoCircle
          content="Delete"
          mouseEnterDelay={0.5}
        >
          <button
            className="w-6 h-6 rounded-full text-mars+2 ml-0.5 hover:bg-white"
            onClick={() => {
              const newBreakdowns = breakdowns.filter(
                (v) => v.key !== breakdown.key,
              );

              prettyLogEventPage({
                domain: "Minimized Top Bar",
                action: "onSelect",
                additionalActionInfo: "Delete breakdown",
                data: {
                  newBreakdowns,
                  pageContext,
                },
              });
              //@ts-ignore
              setBreakdowns(newBreakdowns);
            }}
          >
            <i className="fa-regular fa-sharp fa-trash text-xs" />
          </button>
        </GenericTooltip>
      )}
    </div>
  );
};
